import React from 'react';
import PropTypes from 'prop-types';
import startcase from 'lodash.startcase';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ReactComponent as RadioOn } from '../../assets/images/svg/radio-on.svg';
import { ReactComponent as RadioOff } from '../../assets/images/svg/radio-off.svg';
import { ReactComponent as Vault } from '../../assets/images/svg/vault.svg';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { productLocationTypes } from '../../util/enum/api/productTypes';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import { uButtonClear, uOutlineNone } from '../../assets/styles/utility';
import Paragraph from '../Paragraph/Paragraph';

const getVaultSelectContainerStyles = (isVaultSelected) => {
  if (isVaultSelected) {
    return `
    border-color: ${themeColors.colorTextSecondary};
    `;
  }
};

export const VaultSelectContainer = styled.div`
  position: relative;
  padding: ${pxToRem(8)} 0;
  cursor: pointer;
  border: 1px solid transparent;
  background-color: ${themeColors.colorBackgroundSecondary};
  border-radius: ${variables.borderRadius.borderRadius};
  margin-bottom: ${pxToRem(12)};
  padding-right: ${pxToRem(16)};
  ${({ isVaultSelected }) => getVaultSelectContainerStyles(isVaultSelected)};

  &:nth-child(even) {
    ${({ isVaultSelected }) => getVaultSelectContainerStyles(isVaultSelected)};
  }

  svg {
    width: ${pxToRem(16)};
    margin: 0 ${pxToRem(8)};
  }

  button {
    cursor: pointer;
  }

  &:hover {
    background-color: ${themeColors.colorTableEvenRowHoverBackground};
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRemMd(14)};

    svg {
      width: ${pxToRemMd(16)};
      margin: 0 ${pxToRemMd(8)};
    }
  }
`;

export const VaultSelectButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

export const VaultSelectIndicator = styled.div`
  display: flex;
  align-items: center;
`;

export const VaultSelectValue = styled.p`
  font-size: ${pxToRem(14)};
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  display: block;
  text-align: left;
`;

export const VaultSelectPrice = styled.p`
  font-size: ${pxToRem(14)};
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  display: block;
  text-align: left;
`;

export const VaultSelectTiers = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: flex;
  align-items: center;
  justify-content: flex-end;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${pxToRemMd(14)};
  }
`;

const VaultIconWrapper = styled.div`
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${pxToRem(6)};

  > svg {
    width: ${pxToRem(24)};
    height: ${pxToRem(24)};
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: ${pxToRem(28)};
    height: ${pxToRem(28)};

    > svg {
      width: ${pxToRem(20)};
      height: ${pxToRem(20)};
    }
  }
`;

const BestPriceTag = styled.h4`
  background-color: ${themeColors.colorSuccess};
  padding: ${pxToRem(2)} ${pxToRem(6)};
  font-size: ${pxToRem(12)};
  color: ${themeColors.colorWhite};
  border-radius: 0 3px 0 2px;
  position: absolute;
  top: 0;
  right: 0;
`;

const VaultPriceDifference = styled(Paragraph)`
  font-size: ${pxToRem(14)};
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  text-align: right;
  align-content: center;
`;

const SingleVault = ({
  isVaultSelected,
  isTestProduct,
  vault,
  selectVault,
  // isLowestPriceVault,
  suppressBestPriceTag,
}) => {
  const { t } = useTranslation();

  if (isEmpty(vault)) {
    return null;
  }

  const { LocationType, LocationPrice, BestLocationPriceDifference } = vault;

  return (
    <>
      <VaultSelectContainer isVaultSelected={isVaultSelected}>
        {vault?.IsBestLocationPrice && !suppressBestPriceTag && (
          <BestPriceTag>{t('buyWizard.bestPrice')}</BestPriceTag>
        )}
        <VaultSelectButton onClick={() => selectVault(vault)}>
          <VaultSelectIndicator>
            {isVaultSelected ? <RadioOn /> : <RadioOff />}
            <VaultIconWrapper>
              <Vault />
            </VaultIconWrapper>
            <div>
              <VaultSelectValue>
                {isTestProduct
                  ? `${startcase(
                      parseEnumType(productLocationTypes, LocationType),
                    )} (test)`
                  : startcase(
                      parseEnumType(productLocationTypes, LocationType),
                    )}
              </VaultSelectValue>
              <VaultSelectPrice>
                {formatMoneyNumeral(LocationPrice)}
              </VaultSelectPrice>
            </div>
          </VaultSelectIndicator>
          {BestLocationPriceDifference > 0 && (
            <VaultPriceDifference>
              +{formatMoneyNumeral(BestLocationPriceDifference)}
            </VaultPriceDifference>
          )}
        </VaultSelectButton>
      </VaultSelectContainer>
    </>
  );
};

SingleVault.propTypes = {
  isVaultSelected: PropTypes.bool,
  isTestProduct: PropTypes.bool,
  vault: PropTypes.shape({
    SymbolCode: PropTypes.string,
    LocationType: PropTypes.number,
    PricePerUnit: PropTypes.number,
    IsBestLocationPrice: PropTypes.bool,
    LocationPrice: PropTypes.number,
    BestLocationPriceDifference: PropTypes.number,
  }),
  selectVault: PropTypes.func,
  // isLowestPriceVault: PropTypes.bool,
  suppressBestPriceTag: PropTypes.bool,
};

export default SingleVault;
